'use client';

import { useCallback, useEffect } from 'react';
import { singletonHook } from 'react-singleton-hook';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useShallow } from 'zustand/react/shallow';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';

const LOCAL_STORAGE_KEY = 'displayDatesAsTimestamp';

const useSyncLocalStorageDateDisplayAsTimestamp = singletonHook(
	() => {},
	() => {
		const set = useDriftStore((s) => s.set);
		const [localStorageValue] = useSyncLocalStorage<boolean>(
			LOCAL_STORAGE_KEY,
			false
		);

		const displayDatesAsTimestamp = useDriftStore(
			useShallow((s) => s.displayDatesAsTimestamp)
		);

		// Sync localStorage value to store on mount and when localStorage changes
		useEffect(() => {
			if (localStorageValue !== displayDatesAsTimestamp) {
				set((s) => {
					s.displayDatesAsTimestamp = localStorageValue ?? false;
				});
			}
		}, [localStorageValue, displayDatesAsTimestamp, set]);
	}
);

/**
 * Maintains a local storage state for whether to display the date as a timestamp or "x days/weeks/months ago"
 */
const useDateDisplayState = () => {
	useSyncLocalStorageDateDisplayAsTimestamp();
	const [displayDatesAsTimestamp, setStore] = useDriftStore((s) => [
		s.displayDatesAsTimestamp,
		s.set,
	]);
	const [, setLocalStorageValue] = useSyncLocalStorage(
		LOCAL_STORAGE_KEY,
		'false'
	);

	const setDisplayDatesAsTimestampString = useCallback(
		(value: string) => {
			setStore((s) => {
				s.displayDatesAsTimestamp = value === 'true';
			});
			try {
				setLocalStorageValue(value);
			} catch (err) {
				console.error(
					`Error setting ${LOCAL_STORAGE_KEY} in localStorage`,
					err
				);
			}
		},
		[setStore, setLocalStorageValue]
	);

	const toggleDateDisplayType = useCallback(() => {
		const current = useDriftStore.getState().displayDatesAsTimestamp;
		setDisplayDatesAsTimestampString(current ? 'false' : 'true');
	}, [setDisplayDatesAsTimestampString]);

	return {
		displayDatesAsTimestamp,
		displayDatesAsTimestampString: displayDatesAsTimestamp ? 'true' : 'false',
		setDisplayDatesAsTimestampString,
		toggleDateDisplayType,
	};
};

export default useDateDisplayState;
